import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import type { Map as MapInstance } from "maplibre-gl";

import { Breadcrumbs } from "../../../components/Breadcrumbs";
import type { BreadcrumbItem } from "../../../components/Breadcrumbs";
import { MapActionSheet } from "../../../components/map/MapActionSheet";
import { mapFrameClasses } from "../../../components/map/mapFrameClasses";
import { useAuth } from "../../../providers/AuthProvider";
import { ApiError } from "../../../services/apiClient";
import { getLot, restoreLot, type Lot } from "../api/lots";
import { useMapSnapshot } from "../../../components/map/useMapSnapshot";
import {
  listControlPoints,
  type ControlPoint,
} from "../../shared/api/controlPoints";
import { getWorkspace, type Workspace } from "../../workspaces/api/workspaces";

import { PageShell } from "../../shared/layouts/PageShell";
import { PageHeader } from "../../shared/ui/layouts/PageHeader";
import { SectionWithHeader } from "../../shared/ui/SectionWithHeader";
import { SkeletonCard, SkeletonTable } from "../../shared/ui/skeletons";

import {
  ArrowClockwise,
  ArrowLeft,
  ArrowsInSimple,
  ArrowsOutSimple,
  Gauge,
  Eye,
  EyeSlash,
  FileText,
  SlidersHorizontal,
  Ruler,
  MapPin,
  CrosshairSimple,
  PencilSimple,
  DownloadSimple,
} from "@phosphor-icons/react";
import { defaultIconProps } from "../../shared/ui/icons";
import {
  formatTitleTypeDisplay,
  normalizeTitleType,
  TITLE_TYPE_METADATA,
} from "../utils/titleTypeMetadata";
import { useBreadcrumbBack } from "../../../components/useBreadcrumbBack";
import { mapStyles } from "../../../components/map/createBaseMap";
import {
  LotVerticesMap,
  type LotVerticesMapStatus,
  type LotVertexLike,
  type LotSegmentLike,
} from "../../../components/map/LotVerticesMap";
import { MapShell } from "../../../components/map/MapShell";
import { useMapExpansion } from "../../../components/map/useMapExpansion";
import { useMapPreferences } from "../../../components/map/useMapPreferences";
import { useHaptics } from "../../../hooks/useHaptics";
import { MetricTile } from "../../shared/ui/MetricTile";
import { useThemeMode } from "../../../providers/ThemeProvider";
import { createLogger } from "../../../utils/logger";

const logger = createLogger("LotDetailPage");

const numberFormatter = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const angleFormatter = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 1,
});

type OverlayKey = "angles" | "lengths" | "corners" | "cornerMarkers";
const OVERLAY_DEFAULTS: Record<OverlayKey, boolean> = {
  angles: true,
  lengths: true,
  corners: true,
  cornerMarkers: true,
};
const mapDefaults = { overlays: OVERLAY_DEFAULTS };

type LngLatTuple = [number, number];

const formatClosureRatio = (
  ratio: number | null | undefined,
  closedLabel: string,
): string => {
  if (typeof ratio !== "number" || ratio <= 0) {
    return "—";
  }
  if (!Number.isFinite(ratio)) {
    return closedLabel;
  }
  const display =
    ratio >= 1000 ? Math.round(ratio) : Math.round(ratio * 10) / 10;
  return `1:${display.toLocaleString()}`;
};

const formatSquareMeters = (area?: number | null): string | null => {
  if (typeof area !== "number" || !Number.isFinite(area)) {
    return null;
  }
  return `${numberFormatter.format(area)} m²`;
};

const pageStackClasses = "flex flex-col gap-8";
const actionsStackClasses = "flex flex-wrap items-center gap-3";
const secondaryButtonClasses = "pm-btn pm-btn-secondary";
const primaryButtonClasses = "pm-btn pm-btn-primary";
const ghostButtonClasses = "pm-btn pm-btn-ghost";
const badgeBaseClasses =
  "inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-wide";
const roleBadgeClasses: Record<string, string> = {
  owner: "border-indigo-200 bg-indigo-50 text-indigo-700 dark:border-indigo-900/50 dark:bg-indigo-900/30 dark:text-indigo-300",
  editor: "border-green-200 bg-green-50 text-green-700 dark:border-green-900/50 dark:bg-green-900/30 dark:text-green-300",
  viewer: "border-slate-200 bg-slate-100 text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300",
};
const archivedBadgeClasses =
  "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900/50 dark:bg-rose-900/20 dark:text-rose-300";
const cardGridClasses = "grid gap-6 lg:grid-cols-2";
const cardBaseClasses =
  "flex flex-col gap-6 rounded-3xl border border-slate-200 bg-white p-6 shadow-2xl dark:border-slate-800 dark:bg-slate-900";
const mapSectionClasses = "flex flex-col gap-4";
const statValueClasses = "text-lg font-semibold text-slate-900 dark:text-slate-100";
const statDescriptionClasses = "text-sm text-slate-500 dark:text-slate-400";
const placeholderClasses = "text-sm text-slate-500 dark:text-slate-400";
const detailHeroClasses =
  "rounded-2xl border border-sky-100 bg-gradient-to-r from-sky-50 via-white to-indigo-50 p-5 shadow-inner dark:border-slate-700 dark:from-slate-900 dark:via-slate-900/70 dark:to-slate-800";
const detailGridClasses = "grid gap-3 sm:grid-cols-2";
const detailPillClasses =
  "inline-flex items-center gap-2 self-start rounded-full bg-white/80 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-slate-700 shadow-sm ring-1 ring-slate-200 dark:bg-slate-900/70 dark:text-slate-200 dark:ring-slate-700";
const detailItemClasses =
  "rounded-xl border border-slate-100 bg-white/70 p-4 shadow-sm ring-1 ring-slate-200/50 dark:border-slate-800 dark:bg-slate-900 dark:ring-slate-800/60";
const badgeRowClasses = "flex flex-wrap gap-2";
const tableWrapperClasses =
  "overflow-x-auto rounded-3xl border border-slate-200 bg-white shadow-xl dark:border-slate-800 dark:bg-slate-900";
const tableClasses =
  "min-w-full divide-y divide-slate-200 text-left text-sm text-slate-600 dark:divide-slate-800 dark:text-slate-400";
const tableHeaderCellClasses =
  "bg-slate-50 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:bg-slate-800 dark:text-slate-400";
const tableCellClasses = "px-4 py-2";
const errorBannerClasses =
  "rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-900/50 dark:bg-rose-900/20 dark:text-rose-300";
const mapCanvasBaseClasses = mapFrameClasses.canvas;
const mapCanvasExpandedClasses = mapFrameClasses.canvasExpanded;
const fabButtonClasses = mapFrameClasses.fabButton;
const fabSpinClasses = mapFrameClasses.fabSpin;
const mapPlaceholderClasses = "mt-3 text-sm text-slate-500 dark:text-slate-400";
const snapshotErrorClasses = mapFrameClasses.snapshotError;
const overlayListClasses = "flex flex-col gap-3";
const overlayButtonBaseClasses =
  "flex w-full items-center justify-between rounded-2xl border px-4 py-3 text-left text-sm transition";
const overlayButtonActiveClasses =
  "border-slate-900 bg-slate-900/90 text-white shadow-lg dark:border-slate-100 dark:bg-slate-100/90 dark:text-slate-900";
const overlayButtonInactiveClasses =
  "border-slate-200 bg-white text-slate-800 hover:border-slate-300 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:border-slate-600";
const overlayStatusClasses =
  "inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/40 bg-white/10 text-white dark:border-black/10 dark:bg-black/5";

const EMPTY_VERTICES: LotVertexLike[] = [];
const EMPTY_SEGMENTS: LotSegmentLike[] = [];

export function LotDetailPage() {
  const { t } = useTranslation();

  const { workspaceId, lotId } = useParams<{
    workspaceId: string;
    lotId: string;
  }>();
  const { accessToken, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [lot, setLot] = useState<Lot | null>(null);
  const [controlPoint, setControlPoint] = useState<ControlPoint | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isMutating, setIsMutating] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const {
    isExpanded: isMapExpanded,
    toggle: toggleMapExpansion,
  } = useMapExpansion();
  const { theme } = useThemeMode();
  const mapStyle = useMemo(
    () => (theme === "dark" ? mapStyles.dark : mapStyles.light),
    [theme],
  );
  const { prefs: mapPrefs, setPrefs: setMapPrefs } = useMapPreferences(
    `map-prefs:${user?.id ?? "anon"}:lot-detail`,
    mapDefaults,
  );
  const overlayVisibility = mapPrefs.overlays;
  const [mapStatus, setMapStatus] = useState<LotVerticesMapStatus>({
    isMapReady: false,
    hasRenderedTiles: false,
  });

  const mapInstanceRef = useRef<MapInstance | null>(null);
  const inFlightKeyRef = useRef<string | null>(null);
  const loadedKeyRef = useRef<string | null>(null);

  const initialWorkspace = (location.state as { workspace?: Workspace } | undefined)
    ?.workspace;
  const [workspaceDetails, setWorkspaceDetails] = useState<Workspace | undefined>(
    initialWorkspace,
  );
  const workspaceLinkState = useMemo(
    () => (workspaceDetails ? { workspace: workspaceDetails } : undefined),
    [workspaceDetails],
  );

  const exportFileBase = useMemo(() => {
    if (!lot) {
      return "lot-map";
    }
    const candidates: string[] = [];
    const trimmedLotName = lot.name.trim();
    if (trimmedLotName) {
      candidates.push(trimmedLotName);
    }
    if (lot.title_no && lot.title_no.trim()) {
      candidates.push(lot.title_no.trim());
    }
    candidates.push(lot.id);

    for (const candidate of candidates) {
      const slug = candidate
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
      if (slug) {
        return slug;
      }
    }
    return `lot-${lot.id.slice(0, 8)}`;
  }, [lot]);

  const loadData = useCallback(async (force = false) => {
    if (!accessToken || !workspaceId || !lotId) {
      return;
    }
    const dataKey = `${workspaceId}:${lotId}:${accessToken}`;
    if (!force) {
      if (inFlightKeyRef.current === dataKey || loadedKeyRef.current === dataKey) {
        return;
      }
    }
    inFlightKeyRef.current = dataKey;
    setIsLoading(true);
    setError(null);
    try {
      const [lotResponse, controlPoints] = await Promise.all([
        getLot(accessToken, lotId),
        listControlPoints(accessToken),
      ]);
      setLot(lotResponse);
      const cp =
        controlPoints.find((ctrl) => ctrl.id === lotResponse.tie_point_id) ??
        null;
      setControlPoint(cp);
      loadedKeyRef.current = dataKey;
    } catch (err) {
      if (err instanceof ApiError && (err.status === 403 || err.status === 404)) {
        navigate(`/properties/${lotId}`);
        return;
      }
      const message =
        err instanceof ApiError
          ? err.message
          : "Failed to load lot details. Please try again.";
      setError(message);
      loadedKeyRef.current = null;
    } finally {
      setIsLoading(false);
      inFlightKeyRef.current = null;
    }
  }, [accessToken, workspaceId, lotId, navigate]);

  const loadWorkspaceDetails = useCallback(async () => {
    if (!accessToken || !workspaceId) {
      return;
    }
    try {
      const details = await getWorkspace(accessToken, workspaceId);
      setWorkspaceDetails(details);
    } catch (err) {
      logger.warn("Failed to load workspace", err);
    }
  }, [accessToken, workspaceId]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  useEffect(() => {
    if (!workspaceId || !accessToken) {
      return;
    }
    if (!workspaceDetails || !workspaceDetails.role) {
      void loadWorkspaceDetails();
    }
  }, [accessToken, workspaceDetails, workspaceId, loadWorkspaceDetails]);

  const workspaceRole = workspaceDetails?.role;
  const roleBadgeVariant = workspaceRole ?? "viewer";
  const roleBadgeLabel = workspaceRole
    ? workspaceRole.charAt(0).toUpperCase() + workspaceRole.slice(1)
    : workspaceDetails
      ? "Viewer"
      : null;
  const canEditLots = workspaceRole === "owner" || workspaceRole === "editor";

  const vertexMeta = useMemo(() => {
    const map = new Map<number, { angle: number; coord: LngLatTuple }>();
    const orderedSeqs: number[] = [];
    lot?.vertices?.forEach((vertex) => {
      if (
        vertex.seq_no >= 1 &&
        typeof vertex.interior_angle_deg === "number" &&
        Number.isFinite(vertex.interior_angle_deg) &&
        Number.isFinite(vertex.longitude) &&
        Number.isFinite(vertex.latitude)
      ) {
        map.set(vertex.seq_no, {
          angle: vertex.interior_angle_deg,
          coord: [vertex.longitude, vertex.latitude],
        });
        orderedSeqs.push(vertex.seq_no);
      }
    });
    orderedSeqs.sort((a, b) => a - b);
    return { map, orderedSeqs };
  }, [lot?.vertices]);

  useEffect(() => {
    if (mapInstanceRef.current) {
      mapInstanceRef.current.resize();
    }
  }, [isMapExpanded]);

  const segmentAngleMap = useMemo(() => {
    const map = new Map<number, number>();
    if (!lot?.segments || vertexMeta.orderedSeqs.length === 0) {
      return map;
    }
    const firstSeq = vertexMeta.orderedSeqs[0];
    const lastSeq =
      vertexMeta.orderedSeqs[vertexMeta.orderedSeqs.length - 1] ?? firstSeq;
    lot.segments.forEach((segment) => {
      if (segment.is_tie) {
        return;
      }
      const targetSeq = segment.seq_no + 1;
      let entry =
        vertexMeta.map.get(targetSeq) ??
        (typeof lastSeq === "number" && targetSeq > lastSeq
          ? vertexMeta.map.get(firstSeq)
          : undefined);
      if (!entry && targetSeq === 1) {
        entry = vertexMeta.map.get(firstSeq);
      }
      if (entry) {
        map.set(segment.seq_no, entry.angle);
      }
    });
    return map;
  }, [lot?.segments, vertexMeta]);

  const hasAngleData = segmentAngleMap.size > 0;

  const hasLengthData = useMemo(() => {
    if (!lot?.segments || !lot.vertices || lot.vertices.length < 2) {
      return false;
    }
    const hasValidSegment = lot.segments.some(
      (segment) =>
        !segment.is_tie &&
        typeof segment.distance_m === "number" &&
        Number.isFinite(segment.distance_m),
    );
    const validVertexCount = lot.vertices.filter(
      (vertex) =>
        Number.isFinite(vertex.longitude) && Number.isFinite(vertex.latitude),
    ).length;
    return hasValidSegment && validVertexCount >= 2;
  }, [lot?.segments, lot?.vertices]);

  const hasCornerData = useMemo(() => {
    return Array.isArray(lot?.vertices) && lot.vertices.length > 0;
  }, [lot?.vertices]);

  const hasCornerMarkerData = useMemo(() => {
    if (!Array.isArray(lot?.vertices) || lot.vertices.length === 0) {
      return false;
    }
    return lot.vertices.some(
      (vertex) =>
        Number.isFinite(vertex.longitude) && Number.isFinite(vertex.latitude),
    );
  }, [lot?.vertices]);

  const overlayOptions = useMemo(
    () => [
      {
        key: "corners" as const,
        label: t("workspaces.lot_detail.overlay_corners"),
        available: hasCornerData,
      },
      {
        key: "cornerMarkers" as const,
        label: t("workspaces.lot_detail.overlay_corner_markers"),
        available: hasCornerMarkerData,
      },
      {
        key: "angles" as const,
        label: t("workspaces.lot_detail.overlay_angles"),
        available: hasAngleData,
      },
      {
        key: "lengths" as const,
        label: t("workspaces.lot_detail.overlay_lengths"),
        available: hasLengthData,
      },
    ],
    [hasAngleData, hasCornerData, hasCornerMarkerData, hasLengthData, t],
  );
  const availableOverlays = overlayOptions.filter((option) => option.available);

  const toggleOverlay = (key: OverlayKey) => {
    setMapPrefs((prev) => ({
      ...prev,
      overlays: {
        ...prev.overlays,
        [key]: !prev.overlays[key],
      },
    }));
  };

  const {
    saveSnapshot,
    isSavingSnapshot,
    snapshotError,
  } = useMapSnapshot({
    mapRef: mapInstanceRef,
    getFileName: () => `${exportFileBase}-snapshot`,
    getErrorMessage: (err) =>
      err instanceof Error
        ? err.message
        : t("workspaces.lot_detail.map_save_error"),
    fallbackError: t("workspaces.lot_detail.map_export_error"),
    blobErrorMessage: t("workspaces.lot_detail.map_export_failed"),
  });

  const displayName = useMemo(() => {
    if (!lot) {
      return "Lot details";
    }
    const trimmedName = lot.name.trim();
    return trimmedName || lot.title_no || "Lot details";
  }, [lot]);

  const breadcrumbItems = useMemo<BreadcrumbItem[]>(() => {
    return [
      { label: t("workspaces.my_workspaces.title"), to: "/workspaces" },
      {
        label: workspaceDetails?.name ?? t("workspaces.lots.page_title_fallback"),
        to: workspaceId ? `/workspaces/${workspaceId}/lots` : undefined,
        state: workspaceLinkState,
      },
      {
        label: "Lots",
        to: workspaceId ? `/workspaces/${workspaceId}/lots` : undefined,
        state: workspaceLinkState,
      },
      { label: displayName },
    ];
  }, [displayName, t, workspaceDetails?.name, workspaceId, workspaceLinkState]);

  const handleBack = useBreadcrumbBack({
    items: breadcrumbItems,
    fallback:
      workspaceId != null
        ? {
            to: `/workspaces/${workspaceId}/lots`,
            state: workspaceLinkState,
          }
        : undefined,
  });
  const triggerHaptic = useHaptics();
  const renderHeaderLabel = (longKey: string, shortKey: string) => (
    <>
      <span className="hidden lg:inline">{t(longKey)}</span>
      <span className="lg:hidden">{t(shortKey)}</span>
    </>
  );
  const isTieCornerLabel = (label?: string | null) => {
    if (!label) {
      return false;
    }
    return label.trim().toLowerCase() === "tie point";
  };
  const renderCornerLabel = (label?: string | null) => {
    if (!label || !label.trim()) {
      return t("common.table.empty_value");
    }
    if (isTieCornerLabel(label)) {
      return (
        <span title={t("workspaces.lot_detail.tie_point_label")}>T*</span>
      );
    }
    return label;
  };
  const renderTypeLabel = (isTie: boolean) => {
    const titleKey = isTie
      ? "workspaces.lot_detail.segments_type_tie"
      : "workspaces.lot_detail.segments_type_boundary";
    const label = isTie ? "T*" : "B*";
    return <span title={t(titleKey)}>{label}</span>;
  };

  if (isLoading) {
    return (
      <PageShell width="wide">
        <div className={pageStackClasses}>
          <Breadcrumbs items={breadcrumbItems} />
          <SkeletonCard className="min-h-[200px]" lines={4} showAvatar />
          <SkeletonTable rows={4} columns={3} />
        </div>
      </PageShell>
    );
  }

  const handleEdit = () => {
    if (!workspaceId || !lotId || !canEditLots || (lot && lot.archived_at)) {
      return;
    }
    navigate(`/workspaces/${workspaceId}/lots/${lotId}/edit`, {
      state: workspaceLinkState,
    });
  };

  const handleRestore = async () => {
    if (!accessToken || !lotId || !canEditLots) {
      return;
    }
    try {
      setIsMutating(true);
      setError(null);
      await restoreLot(accessToken, lotId);
      await loadData(true);
      triggerHaptic("medium");
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : t("workspaces.lot_detail.restore_error");
      setError(message);
    } finally {
      setIsMutating(false);
    }
  };

  if (error || !lot) {
    return (
      <PageShell width="wide">
        <div className={pageStackClasses}>
          <Breadcrumbs items={breadcrumbItems} />
          <PageHeader
            title={displayName}
            subtitle={t("workspaces.lot_detail.subtitle")}
            actions={
              <div className={actionsStackClasses}>
                <button type="button" className={ghostButtonClasses} onClick={handleBack}>
                  <ArrowLeft {...defaultIconProps} aria-hidden />
                  {t("actions.back")}
                </button>
              </div>
            }
          />
          <p className={errorBannerClasses}>
            {error ?? t("workspaces.lot_detail.not_found")}
          </p>
        </div>
      </PageShell>
    );
  }

  const tiePoint = controlPoint;
  const normalizedTitleType = normalizeTitleType(lot.title_type);
  const titleTypeMeta = TITLE_TYPE_METADATA[normalizedTitleType];
  const startVertex = lot.vertices?.[0];
  const isArchived = Boolean(lot.archived_at);
  const headerArea =
    lot.computed_area_m2 ?? lot.stated_area_m2 ?? null;
  const headerAreaLabel = formatSquareMeters(headerArea);
  const lotNumberDisplay = lot.lot_number?.trim() || "";
  const planNumberDisplay = lot.plan_number?.trim() || "";

  return (
    <PageShell width="wide">
      <div className={pageStackClasses}>
        <Breadcrumbs items={breadcrumbItems} />

        <PageHeader
          title={`${displayName}${headerAreaLabel ? ` (${headerAreaLabel})` : ""}`}
          subtitle={t("workspaces.lot_detail.subtitle")}
          description={
            lot.title_no
              ? `${t("workspaces.lot_detail.title_number_label")}: ${lot.title_no}`
              : undefined
          }
          badge={
            roleBadgeLabel ? (
              <span
                className={`${badgeBaseClasses} ${
                  roleBadgeClasses[roleBadgeVariant] ?? roleBadgeClasses.viewer
                }`}
              >
                {roleBadgeLabel}
              </span>
            ) : null
          }
          actions={
            <div className={actionsStackClasses}>
              {lotId ? (
                <Link
                  to={`/properties/${lotId}`}
                  className={ghostButtonClasses}
                >
                  <Eye {...defaultIconProps} aria-hidden />
                  {t("workspaces.lot_detail.view_public", "View public page")}
                </Link>
              ) : null}
              <Link
                to={
                  workspaceId && lotId
                    ? `/workspaces/${workspaceId}/lots/${lotId}/documents`
                    : `/workspaces/${workspaceId ?? ""}/lots`
                }
                className={secondaryButtonClasses}
              >
                <FileText {...defaultIconProps} aria-hidden />
                {t("workspaces.lot_detail.documents_button")}{" "}
                {lot?.agg_documents_count != null ? `(${lot.agg_documents_count})` : ""}
              </Link>
              {canEditLots ? (
                isArchived ? (
                  <button
                    type="button"
                    className={secondaryButtonClasses}
                    onClick={handleRestore}
                    disabled={isMutating}
                  >
                    <ArrowClockwise {...defaultIconProps} aria-hidden />
                    {t("workspaces.lot_detail.restore_button")}
                  </button>
                ) : (
                  <button
                    type="button"
                    className={primaryButtonClasses}
                    onClick={handleEdit}
                  >
                    <PencilSimple {...defaultIconProps} aria-hidden />
                    {t("workspaces.lot_detail.edit_button")}
                  </button>
                )
              ) : null}
              <button type="button" className={ghostButtonClasses} onClick={handleBack}>
                <ArrowLeft {...defaultIconProps} aria-hidden />
                {t("actions.back")}
              </button>
            </div>
          }
        >
          <div className={badgeRowClasses}>
            <span
              className={`${badgeBaseClasses} border-slate-200 bg-slate-100 text-slate-700`}
              title={titleTypeMeta.description}
            >
              {formatTitleTypeDisplay(normalizedTitleType)}
            </span>
          </div>
          {lot.archived_at ? (
            <span className={`${badgeBaseClasses} ${archivedBadgeClasses}`}>
              {t("workspaces.lot_detail.archived_status", {
                date: new Date(lot.archived_at).toLocaleString(),
              })}
            </span>
          ) : null}
        </PageHeader>

        <div className="flex flex-col gap-10">
          <SectionWithHeader
            title={t("workspaces.lot_detail.map_title")}
            description={t("workspaces.lot_detail.map_description")}
          >
            <article className={mapSectionClasses}>
              <MapShell
                mapContent={
                  <LotVerticesMap
                    className={[
                      mapCanvasBaseClasses,
                      isMapExpanded ? mapCanvasExpandedClasses : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    mapRef={mapInstanceRef}
                    mapStyle={mapStyle}
                    vertices={lot?.vertices ?? EMPTY_VERTICES}
                    segments={lot?.segments ?? EMPTY_SEGMENTS}
                    segmentAngles={segmentAngleMap}
                    overlayVisibility={overlayVisibility}
                    fitPadding={48}
                    boundsMaxZoom={19}
                    idPrefix="lot-detail"
                    onStatusChange={setMapStatus}
                  />
                }
                status={mapStatus}
                loadingLabel={t("workspaces.lots.map.loading_map", "Preparing map…")}
                isExpanded={isMapExpanded}
                onToggle={toggleMapExpansion}
                toggleLabels={{
                  expandButton: t("workspaces.lot_detail.map_expand_button"),
                  collapseButton: t("workspaces.lot_detail.map_collapse_button"),
                  expandAria: t("workspaces.lot_detail.map_expand_aria"),
                  collapseAria: t("workspaces.lot_detail.map_collapse_aria"),
                }}
                fabAriaLabel={t("workspaces.lot_detail.map_controls_aria")}
                onZoomIn={() => {
                  const map = mapInstanceRef.current;
                  if (!map) return;
                  const zoom = map.getZoom();
                  if (!Number.isFinite(zoom)) return;
                  map.easeTo({ zoom: zoom + 1, duration: 200 });
                }}
                onZoomOut={() => {
                  const map = mapInstanceRef.current;
                  if (!map) return;
                  const zoom = map.getZoom();
                  if (!Number.isFinite(zoom)) return;
                  map.easeTo({ zoom: zoom - 1, duration: 200 });
                }}
                zoomAria={{
                  in: t("workspaces.lots.map.zoom_in", "Zoom in"),
                  out: t("workspaces.lots.map.zoom_out", "Zoom out"),
                }}
                fabContent={
                  <>
                    <button
                      type="button"
                      className={fabButtonClasses}
                      onClick={() => {
                        void saveSnapshot();
                      }}
                      disabled={isSavingSnapshot || !mapInstanceRef.current}
                      aria-label={t("workspaces.lot_detail.map_save_button")}
                      title={t("workspaces.lot_detail.map_save_button")}
                    >
                      {isSavingSnapshot ? (
                        <ArrowClockwise
                          {...defaultIconProps}
                          className={fabSpinClasses}
                          aria-hidden
                        />
                      ) : (
                        <DownloadSimple {...defaultIconProps} aria-hidden />
                      )}
                    </button>
                    {availableOverlays.length > 0 ? (
                      <button
                        type="button"
                        className={fabButtonClasses}
                        onClick={() => setIsSettingsOpen(true)}
                        aria-label={t("workspaces.lot_detail.overlays_title")}
                      >
                        <SlidersHorizontal {...defaultIconProps} aria-hidden />
                      </button>
                    ) : null}
                  </>
                }
              />
              {(!lot.vertices || lot.vertices.length < 3) ? (
                <p className={mapPlaceholderClasses}>
                  {t("workspaces.lot_detail.map_unavailable")}
                </p>
              ) : null}

              <MapActionSheet
                open={isSettingsOpen}
                onClose={() => setIsSettingsOpen(false)}
                title={t("workspaces.lot_detail.overlays_title")}
              >
                <div
                  className={overlayListClasses}
                  role="group"
                  aria-label={t("workspaces.lot_detail.overlays_aria")}
                >
                  {availableOverlays.map(({ key, label }) => {
                    const isActive = overlayVisibility[key];
                    const Icon = isActive ? EyeSlash : Eye;
                    const shortLabel = (() => {
                      switch (key) {
                        case "corners":
                          return t("workspaces.lot_detail.overlay_corners");
                        case "cornerMarkers":
                          return t("workspaces.lot_detail.overlay_corner_markers");
                        case "angles":
                          return t("workspaces.lot_detail.overlay_angles");
                        case "lengths":
                          return t("workspaces.lot_detail.overlay_lengths");
                        default:
                          return label;
                      }
                    })();
                    return (
                      <button
                        key={key}
                        type="button"
                        className={[
                          overlayButtonBaseClasses,
                          isActive
                            ? overlayButtonActiveClasses
                            : overlayButtonInactiveClasses,
                        ].join(" ")}
                        onClick={() => toggleOverlay(key)}
                        aria-pressed={isActive}
                        aria-label={shortLabel}
                      >
                        <div>
                          <h4 className="text-base font-semibold">{shortLabel}</h4>
                          <small className="text-xs text-slate-500">
                            {isActive
                              ? t("workspaces.lot_detail.overlay_hide")
                              : t("workspaces.lot_detail.overlay_show")}
                          </small>
                        </div>
                        <span className={overlayStatusClasses}>
                          <Icon {...defaultIconProps} size={18} aria-hidden />
                        </span>
                      </button>
                    );
                  })}
                </div>
              </MapActionSheet>
              {snapshotError ? (
                <p className={snapshotErrorClasses}>{snapshotError}</p>
              ) : null}
            </article>
          </SectionWithHeader>

          <SectionWithHeader
            title={t("workspaces.lot_detail.details_title")}
            description={t("workspaces.lot_detail.details_description")}
          >
            <div className={cardGridClasses}>
              <article className={cardBaseClasses}>
                <div className="flex flex-col gap-4">
                  <div className={detailHeroClasses}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="flex flex-col gap-1">
                        <span className="text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-400">
                          {t("workspaces.lot_detail.title_type_label")}
                        </span>
                        <strong className="text-xl font-semibold text-slate-900 dark:text-white">
                          {formatTitleTypeDisplay(normalizedTitleType)}
                        </strong>
                        <p className={statDescriptionClasses}>{titleTypeMeta.description}</p>
                      </div>
                      <div className={detailPillClasses}>
                        <span className="text-slate-500 dark:text-slate-300">
                          {t("workspaces.lot_detail.title_number_label")}
                        </span>
                        <strong className="text-sm text-slate-900 dark:text-slate-100">
                          {lot.title_no?.trim() || t("common.table.empty_value")}
                        </strong>
                      </div>
                    </div>
                  </div>
                  <div className={detailGridClasses}>
                    {lotNumberDisplay ? (
                      <div className={detailItemClasses}>
                        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                          {t("workspaces.lot_detail.lot_number_label")}
                        </span>
                        <p className={statValueClasses}>{lotNumberDisplay}</p>
                      </div>
                    ) : null}
                    {planNumberDisplay ? (
                      <div className={detailItemClasses}>
                        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                          {t("workspaces.lot_detail.plan_number_label")}
                        </span>
                        <p className={statValueClasses}>{planNumberDisplay}</p>
                      </div>
                    ) : null}
                    <MetricTile
                      icon={<MapPin {...defaultIconProps} aria-hidden />}
                      label={t("workspaces.lot_detail.tie_point_label")}
                      value={tiePoint ? tiePoint.code : t("common.table.empty_value")}
                    />
                    {startVertex ? (
                      <MetricTile
                        icon={<CrosshairSimple {...defaultIconProps} aria-hidden />}
                        label={t("workspaces.lot_detail.start_coords_label")}
                        value={`${startVertex.easting.toFixed(2)} ; ${startVertex.northing.toFixed(2)}`}
                      />
                    ) : null}
                  </div>
                </div>
              </article>

              <article className={cardBaseClasses}>
                <h2 className="text-2xl font-semibold text-slate-900">
                  {t("workspaces.lot_detail.metrics_title")}
                </h2>
                <div className="grid gap-4 sm:grid-cols-2">
                  {typeof lot.traverse_length_m === "number" ? (
                    <MetricTile
                      icon={<Ruler {...defaultIconProps} aria-hidden />}
                      label={t("workspaces.lot_detail.traverse_length_label")}
                      value={t("common.units.meters", {
                        value: lot.traverse_length_m.toFixed(2),
                      })}
                      description={t("workspaces.lot_detail.traverse_length_info")}
                    />
                  ) : null}
                  {typeof lot.closure_error_m === "number" ? (
                    <MetricTile
                      icon={<ArrowsInSimple {...defaultIconProps} aria-hidden />}
                      label={t("workspaces.lot_detail.closure_error_label")}
                      value={t("common.units.meters", {
                        value: lot.closure_error_m.toFixed(3),
                      })}
                      description={t("workspaces.lot_detail.closure_error_info")}
                    />
                  ) : null}
                  {typeof lot.computed_area_m2 === "number" ? (
                    <MetricTile
                      icon={<ArrowsOutSimple {...defaultIconProps} aria-hidden />}
                      label={t("workspaces.lot_detail.computed_area_label")}
                      value={t("common.units.square_meters", {
                        value: lot.computed_area_m2.toFixed(2),
                      })}
                      description={t("workspaces.lot_detail.computed_area_info")}
                    />
                  ) : null}
                  {typeof lot.closure_ratio === "number" ? (
                    <MetricTile
                      icon={<Gauge {...defaultIconProps} aria-hidden />}
                      label={t("workspaces.lot_detail.closure_ratio_label")}
                      value={formatClosureRatio(
                        lot.closure_ratio,
                        t("workspaces.lot_detail.closure_ratio_closed"),
                      )}
                      description={t("workspaces.lot_detail.closure_ratio_info")}
                    />
                  ) : null}
                </div>
              </article>
            </div>
          </SectionWithHeader>

          <SectionWithHeader
            title={t("workspaces.lot_detail.segments_title")}
            description={t("workspaces.lot_detail.segments_description")}
          >
            {lot.segments && lot.segments.length > 0 ? (
              <>
                <div className={tableWrapperClasses}>
                  <table className={tableClasses}>
                    <thead>
                      <tr>
                        <th scope="col" className={tableHeaderCellClasses}>
                          {renderHeaderLabel(
                            "workspaces.lot_detail.segments_seq",
                            "workspaces.lot_detail.segments_seq_short",
                          )}
                        </th>
                        <th scope="col" className={tableHeaderCellClasses}>
                          {renderHeaderLabel(
                            "workspaces.lot_detail.segments_from",
                            "workspaces.lot_detail.segments_from_short",
                          )}
                        </th>
                        <th scope="col" className={tableHeaderCellClasses}>
                          {renderHeaderLabel(
                            "workspaces.lot_detail.segments_to",
                            "workspaces.lot_detail.segments_to_short",
                          )}
                        </th>
                        <th scope="col" className={tableHeaderCellClasses}>
                          {renderHeaderLabel(
                            "workspaces.lot_detail.segments_bearing",
                            "workspaces.lot_detail.segments_bearing_short",
                          )}
                        </th>
                        <th scope="col" className={tableHeaderCellClasses}>
                          {renderHeaderLabel(
                            "workspaces.lot_detail.segments_distance",
                            "workspaces.lot_detail.segments_distance_short",
                          )}
                        </th>
                        <th scope="col" className={tableHeaderCellClasses}>
                          {renderHeaderLabel(
                            "workspaces.lot_detail.segments_type",
                            "workspaces.lot_detail.segments_type_short",
                          )}
                        </th>
                        <th scope="col" className={tableHeaderCellClasses}>
                          {renderHeaderLabel(
                            "workspaces.lot_detail.segments_angle",
                            "workspaces.lot_detail.segments_angle_short",
                          )}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {lot.segments.map((segment) => {
                        const angleValue = segment.is_tie
                          ? undefined
                          : segmentAngleMap.get(segment.seq_no);
                        return (
                          <tr key={segment.id}>
                            <td className={tableCellClasses}>{segment.seq_no}</td>
                            <td className={tableCellClasses}>
                              {renderCornerLabel(segment.from_corner)}
                            </td>
                            <td className={tableCellClasses}>
                              {renderCornerLabel(segment.to_corner)}
                            </td>
                            <td className={tableCellClasses}>{segment.bearing}</td>
                            <td className={tableCellClasses}>
                              {segment.distance_m.toFixed(2)}
                            </td>
                            <td className={tableCellClasses}>
                              {renderTypeLabel(segment.is_tie)}
                            </td>
                            <td className={tableCellClasses}>
                              {typeof angleValue === "number"
                                ? angleFormatter.format(angleValue)
                                : t("common.table.empty_value")}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                  {t("workspaces.lot_detail.segments_legend_types")}
                </p>
              </>
            ) : (
              <p className={placeholderClasses}>
                {t("workspaces.lot_detail.segments_empty")}
              </p>
            )}
          </SectionWithHeader>
        </div>

      </div>
    </PageShell>
  );
}
